import EmptyPageContent from '@app/ccf/components/empty-page-content';
import { formatSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { reportFilters } from '@app/components/reports/report-list/reports-filters';
import ReportsTable from '@app/components/reports/report-list/reports-table';
import {
  FilterKey,
  useDataTableQueryState,
} from '@app/hooks/use-data-table-query-state';
import { useGetTimePeriods } from '@shared/api/hooks/reports/periods';
import {
  useCountReportRecords,
  useGetOperationReports,
} from '@shared/api/hooks/reports/reports';
import {
  CompanyReportStatusEnum,
  OperationReportTypeEnum,
  OperationReportsListParams,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { getOverallStatus } from '@shared/lib/utils';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchasedGoodsRoutes } from '../purchased-goods-routes';

export default function PurchaseReportsListPage() {
  const navigate = useNavigate();
  const { page, perPage, sort, ...filterStates } = useDataTableQueryState({
    filters: Object.values(reportFilters).map(
      ({ column }) => column
    ) as FilterKey[],
  });
  const [titleSearch] = useQueryState('title', parseAsString);
  const [shouldPoll, setShouldPoll] = useState(true);
  const { data: periods, status: periodStatus } = useGetTimePeriods();

  // This might need to update to count purchase/sales records separately
  const { mutateAsync: countRecords, status: countStatus } =
    useCountReportRecords();
  const [recordCount, setRecordCount] = useState<number>();
  useEffect(() => {
    async function fetchCount() {
      try {
        const countData = await countRecords({
          data: {
            type: OperationReportTypeEnum.purchase,
          },
        });
        setRecordCount(countData.count);
      } catch (error) {
        setRecordCount(0);
      }
    }
    fetchCount();
  }, [countRecords]);

  const sortParam =
    sort && sort.length > 0 ? formatSortingForEndpoint(sort[0]) : undefined;

  const params: OperationReportsListParams = {
    type: OperationReportTypeEnum.purchase,
    page,
    page_size: perPage,
    search: titleSearch || undefined,
    sort: sortParam,
  };
  Object.values(reportFilters).forEach(({ apiKey, column }) => {
    const value = filterStates[column as keyof typeof filterStates] as
      | string[]
      | undefined;
    if (value?.length) {
      (params as Record<string, unknown>)[apiKey] = value;
    }
  });

  const { data, isFetchedAfterMount, isLoading, isPreviousData, status } =
    useGetOperationReports(params, {
      polling: shouldPoll,
      pollingInterval: 1000,
    });

  // Disable polling if there are no publishing reports
  useEffect(() => {
    if (!isFetchedAfterMount) return;

    if (data && data.results.length > 0) {
      const hasPublishingReports = data.results.some(
        (report) => report.status === CompanyReportStatusEnum.publishing
      );
      if (!hasPublishingReports) setShouldPoll(false);
    }
  }, [data, isFetchedAfterMount]);

  const hasActiveFilters =
    !!titleSearch ||
    Object.values(reportFilters).some(({ column }) => {
      const value = filterStates[column as keyof typeof filterStates] as
        | string[]
        | undefined;
      return !!value?.length;
    });

  const hasNoReports = data && data.results.length === 0 && !hasActiveFilters;
  const hasRecords = !!recordCount || !hasNoReports;
  const hasPeriods = periods && periods.length > 0;

  return (
    <Page
      name="Purchase reports"
      page="page"
      status={getOverallStatus([periodStatus, countStatus])}
    >
      <PageHeader
        name="Purchase reports"
        title="Purchase reports"
        headingTestId="reports-list-heading"
        toolbar={
          !hasNoReports ? (
            <Button
              onClick={() => {
                navigate(purchasedGoodsRoutes.PURCHASE_REPORT_CREATE);
              }}
              disabled={!hasPeriods || !hasRecords}
              data-testid="create-report-button"
            >
              New report
            </Button>
          ) : undefined
        }
        breadcrumbs={[]}
      />
      {(!hasPeriods || !hasRecords) && (
        <EmptyPageContent
          title="No purchase data"
          description={
            <div className="mt-20 space-y-4">
              <Text className="text-center">
                You have not added any purchases. Before creating reports you
                need to import your purchase data.
              </Text>
            </div>
          }
          primaryAction={() =>
            navigate(purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT)
          }
          primaryActionText="Import data"
        />
      )}
      {hasNoReports && hasPeriods && hasRecords && (
        <EmptyPageContent
          title="Create your first purchase report"
          description={
            <div className="space-y-4">
              <Text className="text-center">
                Purchase Reports provide a detailed analysis of the emissions
                associated with the goods your company purchases, allowing you
                to understand the total impact as well as comparing the
                emissions over time or across sites.
              </Text>
              <Text className="text-center">
                You don&rsquo;t have any purchase reports yet. Once you&rsquo;ve
                started your first report you&rsquo;ll see it listed here.
              </Text>
            </div>
          }
          primaryAction={() =>
            navigate(purchasedGoodsRoutes.PURCHASE_REPORT_CREATE)
          }
          primaryActionText="Create purchase report"
          primaryActionTestId="create-report-button"
        />
      )}
      {!hasNoReports && (
        <ReportsTable
          type="card"
          reportType={OperationReportTypeEnum.purchase}
          data={data}
          isFetchedAfterMount={isFetchedAfterMount}
          isLoading={isLoading}
          isPreviousData={isPreviousData}
          status={status}
        />
      )}
    </Page>
  );
}
